import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { array, boolean, number, object, string, union } from 'zod';

/**
 * M2-1 — generative-UI widget catalog (presentational, read-only).
 *
 * Each widget renders ONLY the props the agent supplies. The agent sources those
 * props from the read gateway tools (positions, PnL, risk, orders, alerts); the
 * widgets NEVER fetch their own data and hold no mock/placeholder data. They are
 * rendered inline in the chat transcript via `show_components` → the
 * {@link AgentComponentCatalogService} allowlist → `NgComponentOutlet`.
 *
 * SECURITY: every value is rendered through Angular interpolation / safe property
 * bindings ONLY. No `innerHTML`, no `bypassSecurityTrust*`, no dynamic HTML from
 * agent-supplied data — so a hostile props payload can never inject markup. Each
 * widget Zod-parses its own `props` with resilient `.catch(...)` fallbacks so a
 * malformed field degrades to a placeholder dash rather than throwing.
 */

/** Common signal-input shape: the agent-supplied props bag for one widget. */
type WidgetProps = Record<string, unknown>;

/** Coerce an unknown scalar to a display string, or a dash when absent. */
function display(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? formatNumber(value) : '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  return String(value);
}

/** Locale-grouped number formatting (up to 4 dp), resilient to non-finite input. */
function formatNumber(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(value);
}

// A scalar cell that never throws — objects/arrays collapse to a dash so a nested
// hostile payload can't smuggle markup or blow up the template.
const cell = union([string(), number(), boolean()])
  .catch('')
  .optional();

const titleSchema = string().max(120).catch('').optional();

// ── positions-table ─────────────────────────────────────────────────────────
const PositionRowSchema = object({
    symbol: cell,
    quantity: cell,
    avgPrice: cell,
    marketValue: cell,
    unrealizedPnl: cell,
  })
  .catch({});
const PositionsSchema = object({
  title: titleSchema,
  positions: array(PositionRowSchema).catch([]).optional(),
});

@Component({
  selector: 'app-w-positions-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './positions-table.widget.html',
})
export class PositionsTableWidget {
  readonly props = input<WidgetProps>({});
  protected readonly view = computed(() => {
    const p = PositionsSchema.safeParse(this.props());
    const data = p.success ? p.data : { title: '', positions: [] };
    return {
      title: data.title ?? '',
      rows: (data.positions ?? []).map((r) => ({
        symbol: display(r.symbol),
        quantity: display(r.quantity),
        avgPrice: display(r.avgPrice),
        marketValue: display(r.marketValue),
        unrealizedPnl: display(r.unrealizedPnl),
      })),
    };
  });
}

// ── pnl-summary ──────────────────────────────────────────────────────────────
const PnlSchema = object({
  title: titleSchema,
  currency: string().max(8).catch('').optional(),
  realized: cell,
  unrealized: cell,
  total: cell,
});

@Component({
  selector: 'app-w-pnl-summary',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './pnl-summary.widget.html',
})
export class PnlSummaryWidget {
  readonly props = input<WidgetProps>({});
  protected readonly view = computed(() => {
    const p = PnlSchema.safeParse(this.props());
    const d = p.success ? p.data : {};
    return {
      title: d.title ?? '',
      currency: d.currency ?? '',
      realized: display(d.realized),
      unrealized: display(d.unrealized),
      total: display(d.total),
    };
  });
}

// ── risk-metrics ─────────────────────────────────────────────────────────────
const MetricRowSchema = object({ label: cell, value: cell, unit: cell })
  .catch({});
const RiskSchema = object({
  title: titleSchema,
  metrics: array(MetricRowSchema).catch([]).optional(),
});

@Component({
  selector: 'app-w-risk-metrics',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './risk-metrics.widget.html',
})
export class RiskMetricsWidget {
  readonly props = input<WidgetProps>({});
  protected readonly view = computed(() => {
    const p = RiskSchema.safeParse(this.props());
    const d = p.success ? p.data : { title: '', metrics: [] };
    return {
      title: d.title ?? '',
      rows: (d.metrics ?? []).map((m) => ({
        label: display(m.label),
        value: display(m.value),
        unit: m.unit === undefined || m.unit === '' ? '' : String(m.unit),
      })),
    };
  });
}

// ── orders-table ─────────────────────────────────────────────────────────────
const OrderRowSchema = object({ id: cell, symbol: cell, side: cell, quantity: cell, price: cell, status: cell })
  .catch({});
const OrdersSchema = object({
  title: titleSchema,
  orders: array(OrderRowSchema).catch([]).optional(),
});

@Component({
  selector: 'app-w-orders-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './orders-table.widget.html',
})
export class OrdersTableWidget {
  readonly props = input<WidgetProps>({});
  protected readonly view = computed(() => {
    const p = OrdersSchema.safeParse(this.props());
    const d = p.success ? p.data : { title: '', orders: [] };
    return {
      title: d.title ?? '',
      rows: (d.orders ?? []).map((o) => ({
        symbol: display(o.symbol),
        side: display(o.side),
        quantity: display(o.quantity),
        price: display(o.price),
        status: display(o.status),
      })),
    };
  });
}

// ── alerts-list ──────────────────────────────────────────────────────────────
const AlertRowSchema = object({ severity: cell, message: cell, timestamp: cell })
  .catch({});
const AlertsSchema = object({
  title: titleSchema,
  alerts: array(AlertRowSchema).catch([]).optional(),
});

@Component({
  selector: 'app-w-alerts-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './alerts-list.widget.html',
})
export class AlertsListWidget {
  readonly props = input<WidgetProps>({});
  protected readonly view = computed(() => {
    const p = AlertsSchema.safeParse(this.props());
    const d = p.success ? p.data : { title: '', alerts: [] };
    return {
      title: d.title ?? '',
      rows: (d.alerts ?? []).map((a) => {
        const severity = display(a.severity);
        return {
          severity: severity === '—' ? 'info' : severity,
          message: display(a.message),
          timestamp: display(a.timestamp),
          badgeClass: severityBadge(String(a.severity ?? '').toLowerCase()),
        };
      }),
    };
  });
}

/** Fixed Tailwind class per known severity — never derived from raw HTML. */
function severityBadge(severity: string): string {
  switch (severity) {
    case 'critical':
    case 'error':
    case 'high':
      return 'bg-red-900/50 text-red-300';
    case 'warning':
    case 'warn':
    case 'medium':
      return 'bg-amber-900/50 text-amber-300';
    default:
      return 'bg-slate-700 text-slate-300';
  }
}

// ── facts (key-value) ────────────────────────────────────────────────────────
const FactRowSchema = object({ label: cell, value: cell }).catch({});
const FactsSchema = object({
  title: titleSchema,
  items: array(FactRowSchema).catch([]).optional(),
});

@Component({
  selector: 'app-w-facts',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './facts.widget.html',
})
export class FactsWidget {
  readonly props = input<WidgetProps>({});
  protected readonly view = computed(() => {
    const p = FactsSchema.safeParse(this.props());
    const d = p.success ? p.data : { title: '', items: [] };
    return {
      title: d.title ?? '',
      rows: (d.items ?? []).map((f) => ({ label: display(f.label), value: display(f.value) })),
    };
  });
}
